/**
 * Defines the structure for configuring screen reader text injection.
 * Each object in this interface specifies how and where to add
 * accessibility-focused, visually hidden text to the DOM.
 *
 * @property {string}               parent               - The CSS selector for the DOM element parent which will serve as base for an element query.
 * @property {string}               target               - The CSS selector for the DOM element where the screen reader text will be inserted.
 * @property {'prepend' | 'append'} [attachOn='append']  - Determines whether the screen reader text is added before ('prepend') or after ('append') the target element's existing content. Defaults to 'append'.
 * @property {string}               [setTextFromElement] - An optional CSS selector for an element whose text content will be used as the screen reader text. If provided and found, this takes precedence over `setText`.
 * @property {string}               [setText]            - An optional custom text string to be used as the screen reader text. This is used if `setTextFromElement` is not provided or the referenced element has no text.
 * @property {string}               [setSeparatorText]   - An optional text string to be used as a separator between existing content and the new screen reader text. It is added based on `attachOn` property.
 */
interface ScreenReaderItem {
	parent: string;
	target: string;
	attachOn?: 'prepend' | 'append';
	setTextFromElement?: string;
	setText?: string;
	setSeparatorText?: string;
}

/**
 * Represents a collection of screen reader items.
 *
 * This type alias defines an array where each element is a `ScreenReaderItem` object,
 * typically used for managing accessibility messages to be read aloud by screen readers.
 *
 * @member ScreenReaderItem[]
 */
type ScreenReaderItems = ScreenReaderItem[];

/**
 * A utility class to manage and inject screen reader-only text into the DOM
 * for improved accessibility.
 *
 * It helps in adding descriptive text that is only audible to screen readers,
 * without visually altering the page layout.
 */
class ScreenReaderHandler {
	private items: ScreenReaderItems = [];

	/**
	 * Initializes the ScreenReaderHandler with an array of configuration items.
	 * This method should be called before `setScreenReader`.
	 *
	 * @param {Array<Object>} items An array of configuration objects, each defining how
	 *                              screen reader text should be handled for a specific element.
	 */
	public init(items: ScreenReaderItems) {
		this.items = items;
	}

	/**
	 * Processes the configured items and injects the screen reader-only spans
	 * into the specified target elements in the DOM.
	 */
	public setScreenReader() {
		if (this.items.length === 0) {
			return;
		}

		this.items.forEach((item: ScreenReaderItem) => {
			const targetElements = document.querySelectorAll(`${item.parent} ${item.target}`);

			if (targetElements.length === 0) {
				return;
			}

			targetElements.forEach((targetElement) => {
				let spanText = null;

				if (item.setTextFromElement) {
					const textSourceElement = targetElement
						.closest(item.parent)
						?.querySelector(item.setTextFromElement);

					if (textSourceElement && textSourceElement.textContent) {
						spanText = textSourceElement.textContent.trim();
					}
				}

				if (spanText === null && item.setText) {
					spanText = item.setText;
				}

				if (spanText === null || spanText === '') {
					return;
				}

				if (item.setSeparatorText) {
					if (item.attachOn === 'prepend') {
						spanText = spanText + item.setSeparatorText;
					} else {
						spanText = item.setSeparatorText + spanText;
					}
				}

				const srOnlySpan = document.createElement('span');
				srOnlySpan.classList.add('sr-only');
				srOnlySpan.textContent = spanText;

				if (item.attachOn === 'prepend') {
					targetElement.prepend(srOnlySpan);
				} else {
					targetElement.append(srOnlySpan);
				}
			});
		});
	}
}

export default new ScreenReaderHandler();
